/**
 * pulsebot/llm.ts — client API for PulseBot's LLM fallback layer.
 *
 * This sits BESIDE the deterministic bot in this directory, not instead of it.
 * router.ts handles everything it recognises — instantly and for free — and only
 * unmatched input reaches the Groq-backed Edge Function this module talks to.
 * See router.ts `runPulseBot` for the handoff.
 *
 * Everything intelligent happens in the `pulsebot` Edge Function; this module
 * is the transport plus the CRUD around conversations and settings. In
 * particular the Groq API key is NEVER referenced here — CRA inlines every
 * REACT_APP_* variable into the public bundle, so a key on this side of the
 * wire would be readable by anyone who opens DevTools.
 *
 * Error convention matches apiClient.ts: functions resolve to a result object
 * rather than throwing, so a chat panel can render a failure inline instead of
 * unmounting behind an error boundary.
 *
 * @module lib/pulsebot/llm
 */

import { supabase } from '../supabase';
import { getCurrentWorkspaceId, NO_WORKSPACE_MESSAGE } from '../apiClient';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Settings for the LLM layer. There are no write toggles: the Edge Function
 * ships no write tools at all, so read access and model tuning is the whole
 * surface. Writes live in the deterministic dialog flow.
 */
export interface PulseBotSettings {
  workspace_id: string;
  enabled: boolean;
  model: string;
  temperature: number;
  max_rows: number;
  allow_read: boolean;
  allowed_roles: string[];
  updated_at?: string;
  updated_by?: string | null;
}

export interface ChatResponse {
  ok: true;
  conversation_id: string;
  message: string;
  tools_used: string[];
  usage: { total_tokens: number };
  model: string;
}

export interface ChatFailure {
  ok: false;
  error: string;
}

export type ChatResult = ChatResponse | ChatFailure;

/** A rendered chat bubble. Tool traffic is stored but not shown. */
export interface BotMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string | null;
  created_at: string;
}

export interface BotConversation {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Edge Function transport ─────────────────────────────────────────────────

const FUNCTION_NAME = 'pulsebot';

/**
 * Invoke the Edge Function and normalise its many failure shapes into one.
 *
 * supabase-js reports a non-2xx as a thrown FunctionsHttpError whose useful
 * detail sits in `context.json()`, so a naive `if (error)` surfaces the useless
 * string "Edge Function returned a non-2xx status code" to the user. We dig the
 * real message out — that is the difference between "PulseBot is not configured
 * yet — GROQ_API_KEY is missing" and a shrug.
 */
async function invoke(body: Record<string, unknown>): Promise<ChatResult> {
  try {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body });

    if (error) {
      const ctxResponse = (error as { context?: Response }).context;
      if (ctxResponse && typeof ctxResponse.json === 'function') {
        try {
          const parsed = await ctxResponse.json();
          if (parsed?.error) return { ok: false, error: String(parsed.error) };
        } catch {
          /* body wasn't JSON — fall through to the generic message */
        }
      }
      return { ok: false, error: error.message || 'PulseBot is unreachable.' };
    }

    if (data?.error) return { ok: false, error: String(data.error) };
    return data as ChatResponse;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'PulseBot is unreachable.',
    };
  }
}

/** Send a message. Pass `conversationId` to continue; omit it to start fresh. */
export async function sendMessage(
  message: string,
  conversationId?: string | null,
): Promise<ChatResult> {
  return invoke({
    action: 'chat',
    message,
    conversation_id: conversationId ?? null,
  });
}

// ─── Router entry point ──────────────────────────────────────────────────────

/**
 * The conversation the fallback is currently continuing.
 *
 * Module-level rather than React state because `runPulseBot` is a plain async
 * function called from the panel, and threading an id back out through
 * `BotResult` would put transport bookkeeping into the contract every rule-based
 * handler shares. Scope is one browser session, which matches the panel's own
 * "history persists while the tab is open" behaviour.
 */
let activeConversationId: string | null = null;

/** Start a fresh LLM thread. Called when the user clears the chat. */
export function resetLlmConversation(): void {
  activeConversationId = null;
}

export type LlmAnswer =
  | { ok: true; text: string }
  | { ok: false; error: string };

/**
 * Ask the LLM fallback a question the deterministic router could not place.
 *
 * Keeps the conversation id so follow-ups ("break that down by department")
 * still have context. A failed turn does NOT reset it — a transient network
 * blip shouldn't silently drop the thread the user is mid-way through.
 */
export async function askLlm(question: string): Promise<LlmAnswer> {
  const res = await sendMessage(question, activeConversationId);
  if (!res.ok) return { ok: false, error: res.error };

  activeConversationId = res.conversation_id;
  const text = res.message?.trim();
  if (!text) return { ok: false, error: 'The assistant returned an empty answer.' };
  return { ok: true, text };
}

// ─── Settings ────────────────────────────────────────────────────────────────

/** Defaults used when the settings row hasn't been created yet. */
export const DEFAULT_SETTINGS: PulseBotSettings = {
  // Empty on purpose: these are the defaults for "no row was readable", and the workspace
  // is whatever the signed-in person's is. A constant here would name one company.
  workspace_id: '',
  enabled: true,
  model: 'llama-3.3-70b-versatile',
  temperature: 0.2,
  max_rows: 200,
  allow_read: true,
  allowed_roles: [],
};

/**
 * Read the workspace's settings.
 *
 * Falls back to DEFAULT_SETTINGS when the row is missing or unreadable, so the
 * chat panel still renders rather than blocking on a settings lookup.
 */
export async function getSettings(): Promise<PulseBotSettings> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return DEFAULT_SETTINGS;
  const { data, error } = await supabase
    .from('pulsebot_settings')
    .select('*')
    .eq('workspace_id', workspaceId)
    .maybeSingle();

  if (error || !data) return DEFAULT_SETTINGS;
  return { ...DEFAULT_SETTINGS, ...(data as PulseBotSettings) };
}

/**
 * Update settings. RLS restricts this to admin-tier roles, so a non-admin gets
 * a permission error from Postgres rather than a silent no-op.
 */
export async function updateSettings(
  patch: Partial<Omit<PulseBotSettings, 'workspace_id'>>,
): Promise<{ ok: boolean; error?: string }> {
  // `updated_by` is provenance, not a precondition — a failed session lookup
  // should still let the save through with a null actor rather than blocking an
  // admin from changing a setting. Destructured explicitly so the repo's
  // dropped-error rule is satisfied by intent rather than by omission.
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };

  const { data: session, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) {
    console.warn('[pulsebot] could not resolve actor for settings update:', sessionErr.message);
  }

  const { error } = await supabase
    .from('pulsebot_settings')
    .update({
      ...patch,
      updated_at: new Date().toISOString(),
      updated_by: session.session?.user?.id ?? null,
    })
    .eq('workspace_id', workspaceId);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// ─── Conversations ───────────────────────────────────────────────────────────

/** The current user's conversations, most recently used first. */
export async function listConversations(limit = 20): Promise<BotConversation[]> {
  const { data, error } = await supabase
    .from('pulsebot_conversations')
    .select('id, title, created_at, updated_at')
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error || !data) return [];
  return data as BotConversation[];
}

/**
 * Load a conversation's visible transcript.
 *
 * Tool traffic and bare tool-call turns are filtered out here rather than in
 * the query: they must stay in the table (the Edge Function replays them as
 * model context) but they are noise in the UI.
 */
export async function getMessages(conversationId: string): Promise<BotMessage[]> {
  const { data, error } = await supabase
    .from('pulsebot_messages')
    .select('id, role, content, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error || !data) return [];
  return (data as BotMessage[]).filter(
    (m) => (m.role === 'user' || m.role === 'assistant') && !!m.content?.trim(),
  );
}

/** Soft-delete a conversation — nothing in PulseBot hard-deletes (spec §10). */
export async function deleteConversation(id: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('pulsebot_conversations')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// ─── Suggestions ─────────────────────────────────────────────────────────────

/**
 * Starter prompts shown on an empty conversation.
 *
 * Chosen to demonstrate the three things the bot is for — querying, reporting,
 * summarising — because a blank chat box teaches the user nothing about what it
 * can actually do.
 */
export function starterPrompts(role: string): string[] {
  const common = [
    'How many support tickets are open right now?',
    'Summarise this week’s KPI status by department.',
    'How do I request time off?',
  ];

  if (['platform_owner', 'super_admin', 'admin'].includes(role)) {
    return [
      'Generate a report on open tickets by priority.',
      'Which KPIs are off track this week?',
      'Summarise pending leave requests.',
      'Break down expenses by category.',
    ];
  }
  if (['hr_admin', 'dept_admin'].includes(role)) {
    return [
      'Summarise pending leave requests.',
      'How many candidates are in the pipeline?',
      'Which employees have overdue mandatory training?',
      ...common.slice(2),
    ];
  }
  if (['crm_agent', 'manager'].includes(role)) {
    return [
      'What is the total value of deals in the pipeline?',
      'Break down deals by stage.',
      'Summarise my open tasks.',
      ...common.slice(2),
    ];
  }
  return common;
}
